import { getStoredToken, verifyStoredToken } from './auth';

const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3003').replace(/\/$/, '');

export interface PlaylistItem {
  id: string;
  playlist_id: string;
  video_id: string;
  title: string;
  thumbnail: string | null;
  position: number;
  created_at: string;
}

export interface Playlist {
  id: string;
  name: string;
  created_by: string | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  // /api/playlists devolve apenas a contagem; /api/rooms/:id/playlists inclui items.
  item_count?: number;
  items?: PlaylistItem[];
}

export class LoginRequiredError extends Error {
  constructor() {
    super('login_required');
    this.name = 'LoginRequiredError';
  }
}

/**
 * O JWT vive em memória (o cookie é httpOnly) e pode não estar carregado nesta
 * página — ex.: login feito noutro separador. Tenta /auth/me para o refrescar
 * antes de desistir. /auth/me devolve a sessão validada mas NÃO devolve o
 * token (o cookie httpOnly é a fonte de verdade); nesse caso devolve null e o
 * pedido segue apenas com credentials:'include' — o API Service autentica
 * pelo cookie. Só lança LoginRequiredError quando não há sessão válida.
 */
async function ensureToken(): Promise<string | null> {
  const existing = getStoredToken();
  if (existing) return existing;
  const auth = await verifyStoredToken();
  if (auth?.token) return auth.token;
  if (auth?.user) return null;
  throw new LoginRequiredError();
}

function authHeaders(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchRoomPlaylists(roomId: string, retries = 2): Promise<Playlist[]> {
  try {
    const res = await fetch(`${API_URL}/api/rooms/${encodeURIComponent(roomId)}/playlists`);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return data.playlists || [];
  } catch (err) {
    // Blips transitórios de rede/DNS não devem derrubar as playlists da sala:
    // retenta com backoff antes de desistir.
    if (retries > 0) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      return fetchRoomPlaylists(roomId, retries - 1);
    }
    throw err;
  }
}

export async function linkPlaylistToRoom(roomId: string, playlistId: string): Promise<void> {
  const token = await ensureToken();
  const res = await fetch(
    `${API_URL}/api/rooms/${encodeURIComponent(roomId)}/playlists/${encodeURIComponent(playlistId)}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(token),
    }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || `HTTP ${res.status}`);
  }
}

export async function unlinkPlaylistFromRoom(roomId: string, playlistId: string): Promise<void> {
  const token = await ensureToken();
  const res = await fetch(
    `${API_URL}/api/rooms/${encodeURIComponent(roomId)}/playlists/${encodeURIComponent(playlistId)}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: authHeaders(token),
    }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || `HTTP ${res.status}`);
  }
}

export async function fetchAvailablePlaylists(): Promise<Playlist[]> {
  const res = await fetch(`${API_URL}/api/playlists`);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.playlists || [];
}

export async function fetchMaxRoomPlaylists(): Promise<number> {
  try {
    const res = await fetch(`${API_URL}/api/admin/settings/max-playlists-per-room`);
    if (!res.ok) return 5;
    const data = await res.json();
    return typeof data.max === 'number' ? data.max : 5;
  } catch {
    return 5;
  }
}
